// htag-property-type.ts
// Maps app property types to HTAG API types and determines data availability

import type { HtagPropertyType } from './htag-types'

export type AppPropertyType =
  | 'house'
  | 'townhouse'
  | 'unit'
  | 'apartment'
  | 'duplex'
  | 'commercial'
  | 'vacant_land'
  | 'other'

export type HtagDataAvailability = 'full' | 'partial' | 'none'

export interface PropertyTypeConfig {
  htag_type: HtagPropertyType | null
  availability: HtagDataAvailability
  map_mode: 'streetview' | 'staticmap'
  disclaimer: string | null
}

export const PROPERTY_TYPE_CONFIG: Record<AppPropertyType, PropertyTypeConfig> = {
  house: {
    htag_type: 'house',
    availability: 'full',
    map_mode: 'streetview',
    disclaimer: null,
  },
  townhouse: {
    htag_type: 'house',
    availability: 'full',
    map_mode: 'streetview',
    disclaimer: 'Market data shown is for houses in this LGA, which is the closest available benchmark for townhouses.',
  },
  unit: {
    htag_type: 'unit',
    availability: 'full',
    map_mode: 'streetview',
    disclaimer: null,
  },
  apartment: {
    htag_type: 'unit',
    availability: 'full',
    map_mode: 'streetview',
    disclaimer: null,
  },
  duplex: {
    htag_type: 'house',
    availability: 'partial',
    map_mode: 'streetview',
    disclaimer: 'Market data shown uses house benchmarks for this LGA. Duplex-specific data is not available — use as directional context only.',
  },
  commercial: {
    htag_type: null,
    availability: 'none',
    map_mode: 'staticmap',
    disclaimer: null,
  },
  vacant_land: {
    htag_type: 'house',
    availability: 'partial',
    map_mode: 'staticmap',
    disclaimer: 'Market intelligence shows residential house data for this LGA as area context. Land-specific metrics are not available.',
  },
  other: {
    htag_type: null,
    availability: 'none',
    map_mode: 'staticmap',
    disclaimer: null,
  },
}

// Alias map: normalised input → AppPropertyType key
const TYPE_ALIASES: Record<string, AppPropertyType> = {
  // House variants
  house:                'house',
  residential:          'house',
  villa:                'house',
  acreage:              'house',
  'house_+_granny_flat':'house',
  granny_flat:          'house',
  // Townhouse
  townhouse:            'townhouse',
  // Unit / Apartment
  unit:                 'unit',
  apartment:            'unit',
  // Duplex / multi
  duplex:               'duplex',
  triplex:              'duplex',
  // Commercial / non-residential
  commercial:           'commercial',
  office:               'commercial',
  retail:               'commercial',
  warehouse:            'commercial',
  industrial:           'commercial',
  mixed_use:            'commercial',
  // Vacant land
  vacant_land:          'vacant_land',
  land:                 'vacant_land',
}

export function getPropertyTypeConfig(type: string): PropertyTypeConfig {
  const normalised = type.toLowerCase().replace(/[\s/-]/g, '_')
  const key = TYPE_ALIASES[normalised] ?? ('other' as AppPropertyType)
  return PROPERTY_TYPE_CONFIG[key] ?? PROPERTY_TYPE_CONFIG.other
}

export function isResidential(type: string): boolean {
  const config = getPropertyTypeConfig(type)
  return config.availability !== 'none'
}

export function getHtagType(type: string): HtagPropertyType | null {
  return getPropertyTypeConfig(type).htag_type
}

export function getMapMode(type: string): 'streetview' | 'staticmap' {
  return getPropertyTypeConfig(type).map_mode
}
